import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button, SIZE as BUTTON_SIZE } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { Plus, Send, Search } from 'lucide-react';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { TableContent } from '../LayoutPage';
import { useQuery } from '@tanstack/react-query';
import { defaultInstance } from '../../api/httpClient';
import { useT } from '../../i18n/admin';

// SAM-376: the send flow used to live in a narrow side drawer opened from
// this page. It has moved to its own full-page view at
// /settings/push-notifications/new (see PushNotificationCompose.tsx) —
// this file now only owns the sent-notifications listing.

// --- Styled ---

const Container = styled('div', { backgroundColor: '#fff', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' });
const Header = styled('div', { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0 24px' });
const ToolbarRow = styled('div', { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 24px' });
const Main = styled('div', { flex: 1, overflow: 'auto', padding: '0 24px 24px' });

const SearchWrapper = styled('div', {
  display: 'flex', alignItems: 'center', gap: '8px', flex: '1', maxWidth: '280px',
  backgroundColor: '#EBEBEB', border: '1px solid #EBEBEB', borderRadius: '20px', padding: '7px 14px',
});
const SearchInputEl = styled('input', {
  border: 'none', outline: 'none', background: 'transparent', fontSize: '14px',
  color: '#374151', flex: 1, width: '100%', '::placeholder': { color: '#9CA3AF' },
});

const StatusBadge = styled('span', ({ $status }: { $status: string }) => ({
  padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
  backgroundColor:
    $status === 'general' ? '#EEF2FF' :
    $status === 'announcement' ? '#FEF3C7' :
    $status === 'event' ? '#DBEAFE' :
    $status === 'booking_confirmed' ? '#D1FAE5' :
    $status === 'booking_cancelled' ? '#FEE2E2' :
    $status === 'promo_code' ? '#F3E8FF' : '#F3F4F6',
  color:
    $status === 'general' ? '#3730A3' :
    $status === 'announcement' ? '#92400E' :
    $status === 'event' ? '#1E40AF' :
    $status === 'booking_confirmed' ? '#065F46' :
    $status === 'booking_cancelled' ? '#991B1B' :
    $status === 'promo_code' ? '#6B21A8' : '#6B7280',
}));

const EmptyState = styled('div', {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  padding: '60px 20px', color: '#6B7280', fontSize: '14px',
});

// --- Types ---

interface NotificationRecord {
  id: number;
  title: string;
  short_description: string;
  type: string;
  status: string;
  created_at: string;
}

// --- Hooks ---

const useSentNotifications = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['sent-notifications'],
    queryFn: async () => {
      const response = await defaultInstance({ method: 'GET', url: '/notification?order_by=created_at&order_direction=DESC&limit=100' });
      return ((response as any)?.data?.data ?? (response as any)?.data ?? []) as NotificationRecord[];
    },
  });
  return { notifications: data || [], isLoading };
};

// --- Constants ---

const TYPE_LABELS: Record<string, string> = {
  general: 'General', booking_confirmed: 'Booking Confirmed', booking_cancelled: 'Booking Cancelled',
  promo_code: 'Promo Code', event: 'Event', announcement: 'Announcement',
};

// --- Component ---

export const PushNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, isLoading } = useSentNotifications();
  const { fmt } = useT();

  const [searchQuery, setSearchQuery] = useState('');

  // Filter notifications by search
  const filteredNotifications = useMemo(() => {
    if (!searchQuery.trim()) return notifications;
    const q = searchQuery.toLowerCase();
    return notifications.filter(n => n.title.toLowerCase().includes(q) || n.short_description?.toLowerCase().includes(q) || n.type.toLowerCase().includes(q));
  }, [notifications, searchQuery]);

  // Table columns
  const columns = useMemo<MRT_ColumnDef<NotificationRecord>[]>(() => [
    {
      accessorKey: 'title',
      header: 'TITLE',
      size: 280,
      Cell: ({ row }) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '13px' }}>{row.original.title}</div>
          {row.original.short_description && (
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: 2, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.original.short_description}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: 'TYPE',
      size: 150,
      Cell: ({ cell }) => <StatusBadge $status={cell.getValue<string>()}>{TYPE_LABELS[cell.getValue<string>()] || cell.getValue<string>()}</StatusBadge>,
    },
    {
      accessorKey: 'created_at',
      header: 'SENT AT',
      size: 180,
      Cell: ({ cell }) => {
        const val = cell.getValue<string>();
        if (!val) return '—';
        return <span style={{ fontSize: '12px', color: '#6B7280' }}>{fmt.dateTime(val)}</span>;
      },
    },
  ], [fmt]);

  return (
    <TableContent>
      <Container>
        <Header>
          <HeadingMedium margin={0}>Push Notifications</HeadingMedium>
          <Button
            onClick={() => navigate('/settings/push-notifications/new')}
            startEnhancer={() => <Plus size={16} />}
            size={BUTTON_SIZE.compact}
            overrides={{
              BaseButton: {
                style: { backgroundColor: 'var(--tenant-primary, #111827)', color: '#fff', borderRadius: '8px', ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' } },
              },
            }}
          >
            Notification
          </Button>
        </Header>

        <ToolbarRow>
          <SearchWrapper>
            <Search size={16} color="#9CA3AF" />
            <SearchInputEl placeholder="Search notifications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchWrapper>
        </ToolbarRow>

        <Main>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
          ) : filteredNotifications.length === 0 ? (
            <EmptyState>
              <Send size={40} color="#D1D5DB" style={{ marginBottom: 12 }} />
              <div>{searchQuery ? 'No matching notifications' : 'No notifications sent yet'}</div>
              <div style={{ fontSize: '12px', marginTop: 4 }}>Click "+ Notification" to get started</div>
            </EmptyState>
          ) : (
            <MantineDataTable
              columns={columns}
              data={filteredNotifications}
              enablePagination
              enableSorting
              initialPageSize={20}
              pageSizeOptions={[10, 20, 50]}
            />
          )}
        </Main>
      </Container>
    </TableContent>
  );
};
